import {
  GAME_MODE_CATALOG,
  generateActivityFromRecipe,
  generateActivityWithAi,
  mapGenerateActivityError,
  type GameMode,
  type MusicGenerationPrefs,
} from '@interactive-room';
import type { AICaseRecipe } from './constants/subjectCatalog';
import { getSubjectById } from './constants/subjectCatalog';
import { createDraftId, saveActivityDraft, type ActivityDraft } from './activityStorage';
import type { ExamLevelId } from './constants/examAiParams';
import type { RecipeDifficultyId } from './constants/recipeAiParams';
import type { CourseProfile } from './types/courseProfile';
import { resolveCourseProfile } from './courseContext';

const INLINE_ACTIVITY_AI: Partial<
  Record<GameMode, { extension: string; placeholder: string }>
> = {
  arena: {
    extension:
      'Configura arena (cuestionario en vivo con podio) con 4-6 preguntas multiple-choice sobre el tema. ' +
      'Cada pregunta: consigna concreta, 4 opciones plausibles, correctIndex válido, timeLimitSec:30, points:100. ' +
      'Prohibido "Pregunta 1" u "Opción A" sin contexto temático.',
    placeholder: 'Ej: Repaso sobre la Gran Depresión — crack del 29, New Deal, desempleo…',
  },
  order: {
    extension:
      'Configura order (secuencias) con 4-8 ítems ordenables cronológicos o procedimentales. ' +
      'correctOrder debe ser [0,1,2,...] en el orden lógico correcto. prompt instruccional concreto.',
    placeholder: 'Ej: Ordena las batallas navales de la Segunda Guerra Mundial…',
  },
  vincula: {
    extension:
      'Configura vincula con 4-6 pares ladoA/ladoB temáticos (concepto ↔ definición, causa ↔ efecto, etc.).',
    placeholder: 'Ej: Conectores lógicos — porque/causa, sin embargo/oposición…',
  },
  poll: {
    extension:
      'Configura poll con 2-4 slides de opinión o diagnóstico sobre el tema. 3-5 opciones por slide.',
    placeholder: 'Ej: ¿Qué factor crees que más afectó la crisis de 1929?',
  },
  sandbox: {
    extension:
      'Configura sandbox explora con variant balanza o grafico, availableVariables temáticas y unitLabel acorde al tema.',
    placeholder: 'Ej: Simular factores que debilitaron un imperio…',
  },
  coop: {
    extension: 'Configura coop crossword o anagram con 4-6 palabras/pistas del tema.',
    placeholder: 'Ej: Crucigrama de vocabulario de la Revolución Industrial…',
  },
  point: {
    extension: 'Configura point con prompt para señalar una zona en diagrama (target x,y entre 0 y 1).',
    placeholder: 'Ej: Señala en el mapa la región de Polonia en 1939…',
  },
  wall: {
    extension: 'Configura wall con prompt de debate y 2 posturas (stances).',
    placeholder: 'Ej: ¿El New Deal fue efectivo? Argumenta a favor o en contra.',
  },
};

export interface SubjectContext {
  subjectId: string;
  subjectTag: string;
  subjectExpertPrompt: string;
}

export function resolveSubjectContext(params: {
  subjectId?: string;
  subjectTag?: string;
}): SubjectContext | null {
  const subject = params.subjectId ? getSubjectById(params.subjectId) : undefined;
  const tagFromParam = params.subjectTag?.trim();
  const subjectTag = tagFromParam || subject?.tag || '';
  const subjectId = subject?.id || params.subjectId?.trim() || '';

  if (!subjectId || subjectId === 'general' || !subjectTag || subjectTag === 'general') {
    return null;
  }

  return {
    subjectId,
    subjectTag,
    subjectExpertPrompt: subject?.aiExpertPrompt || '',
  };
}

export function supportsInlineActivityAi(mode: GameMode): boolean {
  return Boolean(INLINE_ACTIVITY_AI[mode]);
}

export function buildInlineActivityRecipe(mode: GameMode, subjectId: string): AICaseRecipe {
  const spec = INLINE_ACTIVITY_AI[mode];
  if (!spec) throw new Error('inline_ai_unsupported_mode');
  const subject = getSubjectById(subjectId);
  if (!subject || subject.id === 'general') throw new Error('subject_context_required');
  const catalog = GAME_MODE_CATALOG[mode];
  return {
    id: `inline-${subjectId}-${mode}`,
    subjectId: subject.id,
    title: catalog?.displayName ?? mode,
    description: catalog?.tagline ?? '',
    icon: catalog?.icon ?? '🎯',
    mode,
    category: 'activador',
    aiSystemPromptExtension: spec.extension,
    placeholderText: spec.placeholder,
  };
}

export interface GenerateSubjectAwareActivityParams {
  mode: GameMode;
  subjectId?: string;
  subjectTag?: string;
  /** Receta completa desde el catálogo de asignatura (StudioSubjectPage). */
  recipe?: AICaseRecipe;
  level?: ExamLevelId;
  difficulty?: RecipeDifficultyId;
  tuning?: string;
  musicPrefs?: MusicGenerationPrefs;
  /** Perfil explícito; si se omite, resuelve por courseId o perfil activo legacy. */
  courseProfile?: CourseProfile | null;
  /** ID de curso desde la URL — prioridad sobre perfil activo global. */
  courseId?: string | null;
}

export interface GenerateSubjectAwareActivityResult {
  title: string;
  payload: unknown;
  notice?: string;
}

export async function generateSubjectAwareActivity(
  params: GenerateSubjectAwareActivityParams,
): Promise<GenerateSubjectAwareActivityResult> {
  const context = resolveSubjectContext({
    subjectId: params.recipe?.subjectId ?? params.subjectId,
    subjectTag: params.subjectTag,
  });
  if (!context) throw new Error('subject_context_required');

  const recipe =
    params.recipe ??
    buildInlineActivityRecipe(params.mode, context.subjectId);

  const courseProfile = resolveCourseProfile({
    courseProfile: params.courseProfile,
    courseId: params.courseId,
  });

  let title = recipe.title;
  let payload: unknown;
  let notice: string | undefined;

  try {
    const generated = await generateActivityWithAi({
      mode: recipe.mode,
      recipeId: recipe.id,
      recipeTitle: recipe.title,
      recipeExtension: recipe.aiSystemPromptExtension,
      subjectTag: context.subjectTag,
      subjectExpertPrompt: context.subjectExpertPrompt,
      level: params.level,
      difficulty: params.difficulty,
      tuning: params.tuning,
      musicPrefs: params.musicPrefs,
      courseProfile: courseProfile ?? undefined,
    });
    title = generated.title;
    payload = generated.payload;
  } catch (e) {
    const code = e instanceof Error ? e.message : 'generate_activity_failed';
    // Sin inventar 6basico: el local solo corre si ya hay identity real del diálogo/curso.
    if (!params.level) {
      throw e instanceof Error ? e : new Error(code);
    }
    const local = generateActivityFromRecipe({
      recipe,
      subjectTag: context.subjectTag,
      level: params.level,
      difficulty: params.difficulty ?? 'medium',
      tuning: params.tuning,
    });
    title = local.title;
    payload = local.payload;
    notice =
      code === 'mistral_not_configured' || code === 'mistral_auth_failed'
        ? `${mapGenerateActivityError(code)} Se usó el generador local.`
        : `No se pudo generar con IA (${mapGenerateActivityError(code)}) — se usó el generador local.`;
  }

  return { title, payload, notice };
}

export interface GenerateSubjectAwareActivityDraftParams extends GenerateSubjectAwareActivityParams {
  draftId?: string;
  persist?: boolean;
}

export interface GenerateSubjectAwareActivityDraftResult {
  draft: ActivityDraft;
  notice?: string;
}

export async function generateSubjectAwareActivityDraft(
  params: GenerateSubjectAwareActivityDraftParams,
): Promise<GenerateSubjectAwareActivityDraftResult> {
  const context = resolveSubjectContext({
    subjectId: params.recipe?.subjectId ?? params.subjectId,
    subjectTag: params.subjectTag,
  });
  if (!context) throw new Error('subject_context_required');

  const { title, payload, notice } = await generateSubjectAwareActivity(params);

  const draft: ActivityDraft = {
    id: params.draftId ?? createDraftId(),
    mode: params.recipe?.mode ?? params.mode,
    title,
    subjectTag: context.subjectTag,
    payload,
    ...(params.courseId ? { courseId: params.courseId } : {}),
  };

  if (params.persist !== false) {
    saveActivityDraft(draft);
  }

  return { draft, notice };
}
